export type CrmSource = 'customer' | 'carriers' | 'contractor' | 'suppliers';

export type CallStatus = 'initiating' | 'ringing' | 'in_progress' | 'completed' | 'no_answer' | 'failed' | 'missed'
  | 'busy' | 'left_voicemail' | 'wrong_number' | 'dialled';

/** Outcomes a user can pick when logging a call by hand - mirrors
 *  CallLog::MANUAL_OUTCOMES on the backend. */
export type CallOutcome = 'completed' | 'no_answer' | 'busy' | 'left_voicemail' | 'wrong_number';

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  completed: 'Connected',
  no_answer: 'No answer',
  busy: 'Busy',
  left_voicemail: 'Left voicemail',
  wrong_number: 'Wrong number'
};

/** Body for POST crm/messaging/calls - see RingotelVoiceCallController::store(). */
export interface LogCallPayload {
  crm_source: CrmSource;
  subject_uuid: string;
  direction: 'inbound' | 'outbound';
  phone_number: string | null;
  outcome: CallOutcome;
  called_at: string;
  duration_seconds: number | null;
  notes: string | null;
}

/** Body for PUT crm/messaging/calls/{id} - see RingotelVoiceCallController::update(). */
export interface UpdateCallPayload {
  outcome: CallOutcome;
  duration_seconds: number | null;
  notes: string | null;
}

/** Mirrors the backend's call_logs row (app/Models/crm/CallLog.php), plus
 *  'commstack' for rows merged in from Commstack's call log (no DB row
 *  behind those - see RingotelVoiceCallController::presentCommstack()). */
export interface CallLogRecord {
  id: number;
  source: 'ringotel' | 'manual' | 'softphone' | 'webrtc' | 'commstack';
  direction: 'inbound' | 'outbound';
  to_number: string;
  from_number: string | null;
  /** null for a 'commstack' row - Commstack gives no explicit outcome field. */
  status: CallStatus | null;
  duration_seconds: number | null;
  started_at: string | null;
  ended_at: string | null;
  notes: string | null;
  logged_by: string | null;
  created_at: string;
}

/** GET crm/messaging/calls/configured - which calling paths are available
 *  and the number calls go out from. */
export interface CallingConfig {
  /** Server-side Ringotel Voice API. */
  configured: boolean;
  /** In-browser calling from the Call console. */
  webrtc: boolean;
  /** Our registered number every outbound call presents. */
  caller_id: string;
}

/** GET crm/messaging/calls/webrtc-config - SIP line the Call console
 *  registers with. See RingotelVoiceService::webRtcConfig(). */
export interface WebRtcConfig {
  ws_url: string;
  sip_uri: string;
  domain: string;
  username: string;
  password: string;
  display_name: string;
  caller_id: string;
  stun_servers: string[];
}

/** Statuses the Call console reports while a webrtc call runs. */
export type CallProgressStatus = 'ringing' | 'in_progress' | 'completed' | 'no_answer' | 'busy' | 'failed';

/** GET crm/messaging/calls/missed - one inbound call nobody answered. */
export interface MissedCall {
  id: number;
  crm_source: CrmSource | null;
  subject_uuid: string | null;
  from_number: string | null;
  status: 'missed' | 'no_answer';
  at: string | null;
  seen_at: string | null;
  seen_by: string | null;
}
